import clonedeep from 'lodash.clonedeep';
import cloneDeep from 'lodash.clonedeep';
import { PageId, WidgetId } from 'types/idTypes';
import { Page } from 'types/pageTypes';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import { ResponsiveWidgetBaseData } from 'widgets/ResponsiveWidgets/ResponsiveWidgetBase.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, NewWidget, WidgetMap } from 'widgets/Widget.types';

import { ActiveWidget, ClipboardGroupWidget, ClipboardWidget, WidgetClipboard } from '../../store/widgetControlSlice';
import { SCROLL_CONTAINER_ID } from '../PageArea/PageArea.config';
import { COPY_OFFSET_POSITION } from './Clipboard.config';

export const clearBrowserClipboard = () => navigator?.clipboard?.writeText('');

export const shiftWidgetPosition = (widgetData: AllWidgetData) => {
  const newWidgetData = clonedeep(widgetData);

  newWidgetData.topPx = widgetData.topPx + COPY_OFFSET_POSITION;
  newWidgetData.leftPx = widgetData.leftPx + COPY_OFFSET_POSITION;

  return newWidgetData;
};

/**
 * Recursive fn to get all group widgets to copy.
 * For groups with responsive widgets, will get the nested member widget ids.
 *
 * Used in createCopyWidgets
 *
 * @param memberWidgetIds member ids of group
 * @param parentId parent id
 * @param isResponsiveGroup True if it is a responsive widget, if true, will set the appropriate componentKey for the groupCopyWidget
 * @param widgets
 * @returns
 */
export const getClipboardGroupWidgets = (
  memberWidgetIds: WidgetId[],
  parentId: WidgetId,
  isResponsiveGroup: boolean,
  widgets: WidgetMap,
): ClipboardGroupWidget[] => {
  let groupWidgets: ClipboardGroupWidget[] = [];
  memberWidgetIds.forEach((memberId: WidgetId) => {
    const widgetData = widgets[memberId] as AllWidgetData;
    const widgetType = getWidgetTypeFromId(memberId);

    let componentKey: string | undefined = undefined;
    if (isResponsiveGroup) {
      const responsiveWData = widgets[parentId] as ResponsiveWidgetBaseData;
      const { componentWidgetIdMap } = responsiveWData;

      componentKey = Object.keys(componentWidgetIdMap).find((key) => componentWidgetIdMap[key] === memberId);
    }

    // Handle the case where a group widget contains a responsive widget with their own respective memberWidgetIds
    let nestedGroupMembers: ClipboardGroupWidget[] = [];
    if (widgetData.hasOwnProperty('memberWidgetIds')) {
      nestedGroupMembers = getClipboardGroupWidgets(
        (widgetData as ResponsiveWidgetBaseData).memberWidgetIds,
        memberId,
        true,
        widgets,
      );
    }

    groupWidgets.push({
      widgetType,
      widgetData,
      ...(componentKey && { componentKey }),
      ...(nestedGroupMembers.length > 0 && { groupMembers: nestedGroupMembers }),
    });
  });

  return groupWidgets;
};

export const createCopyWidgets = (
  activeWidgets: ActiveWidget[],
  widgets: WidgetMap,
  activePageId: PageId,
  widgetLayerOrder: WidgetId[],
) => {
  const copiedWidgets: WidgetClipboard = [];

  // Order the widgets by the widgetLayerOrder on the page so that they are added with the correct
  // layering when pasted.
  const sortedActiveWidgets = cloneDeep(activeWidgets).sort((w1, w2) => {
    const w1Id = w1.groupId || w1.responsiveGroupId || w1.id;
    const w2Id = w2.groupId || w2.responsiveGroupId || w2.id;

    const w1Idx = widgetLayerOrder.findIndex((id) => id === w1Id);
    const w2Idx = widgetLayerOrder.findIndex((id) => id === w2Id);

    if (w1Idx < w2Idx) return -1;
    if (w1Idx > w2Idx) return 1;
    return 0;
  });

  sortedActiveWidgets.forEach((activeWidget: ActiveWidget) => {
    const { id, groupMembers, groupId, responsiveGroupId } = activeWidget;
    const isGroup = !!groupId && !!groupMembers.length;
    const isResponsiveGroup = !groupId && !!responsiveGroupId && !!groupMembers.length;

    const widgetData = widgets[id] as AllWidgetData;

    const parentId = groupId || responsiveGroupId;

    // copy the group widgets + all the widgets in the group
    if (isGroup || isResponsiveGroup) {
      const groupCopyWidget: ClipboardWidget = {
        widgetType: getWidgetTypeFromId(parentId),
        widgetData: widgets[parentId] as AllWidgetData,
        fromPageId: activePageId,
        groupWidgets: [],
        isResponsiveGroup,
        copyFrom: parentId,
      };

      groupCopyWidget.groupWidgets = getClipboardGroupWidgets(groupMembers, parentId, isResponsiveGroup, widgets);

      copiedWidgets.push(groupCopyWidget);
    } else {
      const widgetType = getWidgetTypeFromId(id);
      copiedWidgets.push({ widgetType, widgetData, fromPageId: activePageId, groupWidgets: [], copyFrom: id });
    }
  });

  return copiedWidgets;
};

/**
 * Recursive fn to generate new group widgets to pass to addWidget action.
 * Handles case where there are nested member widgets for groups that have a responsive widget
 *
 * Used in parseCopyWidgets
 */
const generateNewGroupWidgets = (groupWidgets: ClipboardGroupWidget[], isSamePage: boolean): NewWidget[] => {
  const newGroupWidgets: NewWidget[] = groupWidgets.map((groupWidget) => {
    const { widgetData, widgetType, componentKey, groupMembers } = groupWidget;
    const newWidgetData = isSamePage ? shiftWidgetPosition(widgetData) : widgetData;

    let nestedGroupWidgets: NewWidget[] = [];
    if (groupMembers && groupMembers.length > 0) {
      nestedGroupWidgets = generateNewGroupWidgets(groupMembers, isSamePage);
    }

    return { widgetType, widgetData: newWidgetData, componentKey, groupWidgets: nestedGroupWidgets };
  });

  return newGroupWidgets;
};

export const parseCopyWidgets = (widgetClipboard: WidgetClipboard, activePageId: PageId) => {
  const widgets: NewWidget[] = widgetClipboard.map((clipboardWidget) => {
    const { widgetType, widgetData, fromPageId, isResponsiveGroup, copyFrom } = clipboardWidget;

    const isSamePage = fromPageId === activePageId;
    const { groupWidgets } = clipboardWidget;

    // for groups: shift position of all group member widgets
    if (groupWidgets && groupWidgets.length) {
      const newGroupWidgets = generateNewGroupWidgets(groupWidgets, isSamePage);

      return {
        widgetType,
        widgetData: isResponsiveGroup && isSamePage ? shiftWidgetPosition(widgetData) : widgetData,
        groupWidgets: newGroupWidgets,
        isResponsiveGroup,
        copyFrom,
      };
    }

    const widget = isSamePage
      ? { widgetType, widgetData: shiftWidgetPosition(widgetData), copyFrom }
      : { widgetType, widgetData, copyFrom };

    return widget;
  });

  return widgets;
};

/**
 * Recursive fn to copy member widgets in a page
 * Handles cases for nested widgets
 *
 * @param memberWidgetIds
 * @param widgets
 * @param copiedPageWidgets copiedWidgets - is updated directly with the new copied widgets
 */
const copyAllMemberWidgets = (memberWidgetIds: WidgetId[], widgets: WidgetMap, copiedPageWidgets: WidgetMap) => {
  memberWidgetIds.forEach((memberWidgetId: WidgetId) => {
    const copiedMemberWidget = clonedeep(widgets[memberWidgetId]);
    copiedPageWidgets[memberWidgetId] = copiedMemberWidget;

    if (copiedMemberWidget.hasOwnProperty('memberWidgetIds')) {
      copyAllMemberWidgets((copiedMemberWidget as GroupWidgetData).memberWidgetIds, widgets, copiedPageWidgets);
    }
  });
};

export const createCopyPage = (activePage: Page, widgets: WidgetMap) => {
  const copiedPage = clonedeep(activePage);

  const { widgetLayerOrder } = copiedPage;
  const copiedPageWidgets: WidgetMap = {};

  // copy the pages widgets (can paste the page even if the source page for the copy has been deleted)
  widgetLayerOrder.forEach((wId: WidgetId) => {
    const copiedWidget = clonedeep(widgets[wId]);
    copiedPageWidgets[wId] = copiedWidget;

    // copy the group widgets + all the widgets in the group
    const hasMemberWidgets = widgets[wId]?.hasOwnProperty('memberWidgetIds');
    if (hasMemberWidgets) {
      const { memberWidgetIds } = copiedWidget as GroupWidgetData;
      copyAllMemberWidgets(memberWidgetIds, widgets, copiedPageWidgets);
    }
  });

  // store in clipboard
  return [{ page: copiedPage, widgets: copiedPageWidgets }];
};

/**
 * Get min/max x/y boundaries for the copied widget data
 *
 * @param copyWidgets WidgetClipboard - widgets to get boundary for
 * @returns { minX, minY, maxX, maxY } - widget boundaries
 */
export const getWidgetBounds = (copyWidgets: WidgetClipboard) => {
  const allWidgetData: AllWidgetData[] = copyWidgets.reduce((allWidgets, widget) => {
    if (!widget.groupWidgets?.length || widget.isResponsiveGroup) {
      allWidgets.push(widget.widgetData);
    } else if (widget.groupWidgets?.length && !widget.isResponsiveGroup) {
      // Group widgets - need to add member widgets
      allWidgets.push(...widget.groupWidgets.map((groupWidget) => groupWidget.widgetData));
    }
    return allWidgets;
  }, [] as AllWidgetData[]);

  const bounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } = allWidgetData.reduce(
    (prevBounds, widgetData) => {
      if (widgetData.topPx < prevBounds.minY) prevBounds.minY = widgetData.topPx;
      if (widgetData.topPx + widgetData.heightPx > prevBounds.maxY)
        prevBounds.maxY = widgetData.topPx + widgetData.heightPx;

      if (widgetData.leftPx < prevBounds.minX) prevBounds.minX = widgetData.leftPx;
      if (widgetData.leftPx + widgetData.widthPx > prevBounds.maxX)
        prevBounds.maxX = widgetData.leftPx + widgetData.widthPx;
      return prevBounds;
    },
    { minX: Number.MAX_VALUE, minY: Number.MAX_VALUE, maxX: 0, maxY: 0 },
  );

  return bounds;
};

/**
 * Scrolls page so that `copyWidgets` are in view
 *
 * @param copyWidgets WidgetClipboard
 */
export const scrollToCopiedWidgets = (copyWidgets: WidgetClipboard) => {
  const pageScrollContainer = document.getElementById(SCROLL_CONTAINER_ID);
  if (!pageScrollContainer) return;

  const { scrollTop, scrollLeft, clientHeight, clientWidth } = pageScrollContainer;
  const { minX, minY, maxX, maxY } = getWidgetBounds(copyWidgets);
  let isHorizontalScroll = false;
  let isVerticalScroll = false;

  // Coordinates to scroll page to
  let scrollToY = scrollTop;
  let scrollToX = scrollLeft;

  // Bottom boundary of visible scroll area
  const pageYBound = scrollTop + clientHeight;

  // Right boundary of visible scroll area
  const pageXBound = scrollLeft + clientWidth;

  /**
   * Check the following cases - vertical axis:
   * 1. Widgets are outside of scroll area - top
   * 2. Widgets are cut off by top scroll area boundary
   * 3. Widgets are outside of scroll area - bottom
   * 4. Widgets are cut off by bottom scroll area boundary
   */
  if (
    scrollTop > maxY ||
    (scrollTop > minY && scrollTop < maxY) ||
    pageYBound < minY ||
    (pageYBound < maxY && pageYBound > minY)
  ) {
    isVerticalScroll = true;
    scrollToY = minY;
  }

  /**
   * Check the following cases - horizontal axis:
   * 1. Widgets are outside of scroll area - to the left
   * 2. Widgets are cut off by left scroll area boundary
   * 3. Widgets are outside of scroll area - to the right
   * 4. Widgets are cut off by right scroll area boundary
   */
  if (
    scrollLeft > maxX ||
    (scrollLeft > minX && scrollLeft < maxX) ||
    pageXBound < minX ||
    (pageXBound < maxX && pageXBound > minX)
  ) {
    isHorizontalScroll = true;
    scrollToX = minX;
  }

  if (isVerticalScroll || isHorizontalScroll) {
    pageScrollContainer.scrollTo(scrollToX, scrollToY);
  }
};
